/**
 * One searchable, grouped visa picker for Firm Knowledge: a library
 * document's visas and a skill automation's case filter. None picked means
 * "any visa".
 */
import { Check, ChevronDown, ChevronsUpDown } from "lucide-react";
import { useState, type FC } from "react";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

import { VISA_GROUPS, visaDescription } from "../visaCatalog";

type Props = {
  value: string[];
  onChange: (next: string[]) => void;
  /** Pick one (closes on select) or several. */
  multiple?: boolean;
  id?: string;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  /**
   * `field` is a full-width form control; `inline` reads as text in a meta
   * row (names at most two, then "+N") with a chevron and a hover fill.
   */
  variant?: "field" | "inline";
  testId?: string;
};

export const VisaPicker: FC<Props> = ({
  value,
  onChange,
  multiple = false,
  id,
  disabled,
  placeholder = "Any visa",
  className,
  variant = "field",
  testId = "visa-picker"
}) => {
  const inline = variant === "inline";
  const [open, setOpen] = useState(false);

  const pick = (visa: string) => {
    if (!multiple) {
      onChange(value[0] === visa ? [] : [visa]);
      setOpen(false);
      return;
    }
    onChange(
      value.includes(visa) ? value.filter((v) => v !== visa) : [...value, visa]
    );
  };

  const shown = inline ? 2 : 3;
  const summary =
    value.length === 0
      ? placeholder
      : value.length <= shown
      ? value.join(", ")
      : `${value.slice(0, 2).join(", ")} +${value.length - 2}`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {inline ? (
          <Button
            id={id}
            type="button"
            variant="ghost"
            size="xs"
            role="combobox"
            aria-expanded={open}
            aria-label={`Visas: ${
              value.length ? value.join(", ") : placeholder
            }`}
            disabled={disabled}
            title={value.length > shown ? value.join(", ") : undefined}
            className={cn(
              "-mx-1.5 max-w-72 min-w-0 justify-start text-base",
              value.length === 0 && "text-fg-3",
              className
            )}
            data-testid={testId}
          >
            <span className="truncate">{summary}</span>
            <ChevronDown className="size-3.5 shrink-0" />
          </Button>
        ) : (
          <Button
            id={id}
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className={cn(
              "h-9 w-full justify-between px-3 font-normal",
              value.length === 0 && "text-muted-foreground",
              className
            )}
            data-testid={testId}
          >
            <span className="truncate">{summary}</span>
            <ChevronsUpDown className="size-3.5 shrink-0 opacity-50" />
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent
        className="w-[--radix-popover-trigger-width] min-w-64 p-0"
        align="start"
      >
        <Command>
          <CommandInput placeholder="Search visas, e.g. EB-1A or asylum" />
          <CommandList className="max-h-72">
            <CommandEmpty>No visa by that name.</CommandEmpty>
            <CommandItem
              value="any visa"
              onSelect={() => {
                onChange([]);
                if (!multiple) setOpen(false);
              }}
            >
              <Check
                className={cn("size-3.5", value.length > 0 && "opacity-0")}
              />
              {placeholder}
            </CommandItem>
            {VISA_GROUPS.map((group) => (
              <CommandGroup key={group.id} heading={group.title}>
                {group.visas.map((visa) => {
                  const description = visaDescription(visa);
                  return (
                    <CommandItem
                      key={visa}
                      value={`${visa} ${description ?? ""} ${group.title}`}
                      onSelect={() => pick(visa)}
                      data-testid={`visa-option-${visa}`}
                    >
                      <Check
                        className={cn(
                          "size-3.5",
                          !value.includes(visa) && "opacity-0"
                        )}
                      />
                      <span>{visa}</span>
                      {description ? (
                        <span className="text-xs text-muted-foreground">
                          {description}
                        </span>
                      ) : null}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
